import React, { useEffect, useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { useMediaQuery } from 'react-responsive';
import { 
  FaBalanceScale, 
  FaFileContract, 
  FaHandshake, 
  FaUsers, 
  FaChartLine, 
  FaShieldAlt,
  FaChevronRight,
  FaAward,
  FaBuilding,
  FaStar,
  FaPhoneAlt
} from 'react-icons/fa';
import { Link } from 'react-router-dom';

const DroitSocietes = () => {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"]
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Parallax effects
  const y1 = useTransform(scrollYProgress, [0, 1], [0, 80]);
  const y2 = useTransform(scrollYProgress, [0, 1], [0, -100]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0.5]);

  // Responsive
  const isSmallMobile = useMediaQuery({ maxWidth: 480 });
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const isTablet = useMediaQuery({ maxWidth: 1024 });

  const getFontSize = (desktop, tablet, mobile, smallMobile) => {
    if (isSmallMobile) return smallMobile;
    if (isMobile) return mobile;
    if (isTablet) return tablet;
    return desktop;
  };

  const getSpacing = (desktop, tablet, mobile, smallMobile) => {
    if (isSmallMobile) return smallMobile;
    if (isMobile) return mobile;
    if (isTablet) return tablet;
    return desktop;
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15, delayChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0, opacity: 1,
      transition: { type: "spring", stiffness: 100, damping: 20 }
    }
  };

  const domainesIntervention = [
    {
      icon: <FaFileContract />,
      title: 'Création de sociétés',
      description: 'Choix de la forme juridique, rédaction des statuts, formalités d\'immatriculation et pactes d\'associés.',
    },
    {
      icon: <FaUsers />,
      title: 'Secrétariat Juridique',
      description: 'Tenue des assemblées générales, modifications statutaires, gouvernance et approbation des comptes.',
    },
    {
      icon: <FaHandshake />,
      title: 'Fusions & Acquisitions',
      description: 'Due diligence, négociations contractuelles, cessions de titres et restructurations complexes.',
    },
    {
      icon: <FaChartLine />,
      title: 'Audits juridiques',
      description: 'Analyse des risques, conformité réglementaire, optimisation fiscale et sociale.',
    },
    {
      icon: <FaBalanceScale />,
      title: 'Contentieux sociétaires',
      description: 'Conflits entre associés, mise en jeu de la responsabilité des dirigeants, litiges commerciaux.',
    },
    {
      icon: <FaShieldAlt />,
      title: 'Protection des actifs',
      description: 'Stratégies de protection patrimoniale du dirigeant et sécurisation des investissements.',
    }
  ];

  const stats = [
    { value: "25+", label: "Années d'expérience", icon: <FaAward /> },
    { value: "500+", label: "Sociétés accompagnées", icon: <FaBuilding /> },
    { value: "98%", label: "Satisfaction client", icon: <FaStar /> }
  ];

  return (
    <>
      <Helmet>
        <title>Droit des Sociétés - KMJ & Associés | Expertise Juridique</title>
        <meta name="description" content="Expertise complète en droit des sociétés : création, gestion, fusions-acquisitions, audits juridiques. Cabinet d'avocats KMJ & Associés." />
      </Helmet>

      <motion.section 
        ref={containerRef}
        style={styles.section}
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        {/* Texture de fond luxueuse */}
        <div style={styles.noiseOverlay} />

        {/* Lueur subtile (Premium Light) */}
        <motion.div style={{ ...styles.glowOrb1, y: y1 }} />
        <motion.div style={{ ...styles.glowOrb2, y: y2 }} />

        <div style={styles.container}>
          
          {/* Hero Section */}
          <motion.div style={styles.heroSection} variants={itemVariants}>
            <div style={{
              ...styles.heroContent,
              gridTemplateColumns: isMobile ? '1fr' : '1.1fr 0.9fr',
              gap: getSpacing('5rem', '4rem', '3rem', '2rem'),
              marginBottom: getSpacing('7rem', '5rem', '4rem', '3rem'),
            }}>
              
              <div style={styles.heroTextContainer}>
                <motion.div style={styles.badge} variants={itemVariants}>
                  <span style={styles.badgeText}>Expertise Juridique</span>
                </motion.div>
                
                <motion.div variants={itemVariants}>
                  <h1 style={{
                    ...styles.mainTitle,
                    fontSize: getFontSize('clamp(3rem, 4vw, 4rem)', '2.5rem', '2.2rem', '1.8rem'),
                    marginBottom: '1rem',
                  }}>
                    <span style={styles.titleGradient}>Droit des Sociétés</span>
                  </h1>
                  
                  <p style={{
                    ...styles.subtitle,
                    fontSize: getFontSize('1.4rem', '1.2rem', '1.1rem', '1rem'),
                    marginBottom: '2rem',
                  }}>
                    Stratégie & Sécurisation pour <span style={styles.subtitleAccent}>dirigeants et entreprises</span>
                  </p>
                </motion.div>
                
                <motion.div variants={itemVariants}>
                  <p style={{
                    ...styles.heroText,
                    fontSize: getFontSize('1.1rem', '1rem', '0.95rem', '0.9rem'),
                    marginBottom: '2.5rem',
                  }}>
                    Le cabinet <span style={styles.textHighlight}>KMJ-LAWFIRM</span> offre une expertise pointue en droit des sociétés. 
                    Nous accompagnons les entrepreneurs, les investisseurs et les groupes de sociétés à chaque étape de leur développement, 
                    de la constitution à la transmission, en passant par les restructurations complexes.
                  </p>
                </motion.div>

                {/* Stats Row */}
                <motion.div style={styles.statsContainer} variants={itemVariants}>
                  {stats.map((stat, index) => (
                    <div key={index} style={styles.statItem}>
                      <div style={styles.statIcon}>{stat.icon}</div>
                      <div style={styles.statValue}>{stat.value}</div>
                      <div style={styles.statLabel}>{stat.label}</div>
                    </div>
                  ))}
                </motion.div>
              </div>

              {/* Hero Image */}
              <motion.div 
                style={styles.heroImage}
                variants={itemVariants}
              >
                <div style={{
                  ...styles.imageWrapper,
                  height: isSmallMobile ? '350px' : isMobile ? '450px' : '550px',
                }}>
                  <div style={styles.imageOverlay} />
                  <img
                    src="/images/L1.webp"
                    alt="Droit des sociétés - KMJ & Associés"
                    style={styles.image}
                    loading="eager"
                  />
                  <div style={styles.floatingBadge}>
                    <FaAward style={styles.badgeIconLarge} />
                    <span>Excellence depuis 2024</span>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>

          {/* Detailed Description */}
          <motion.div 
            style={{ ...styles.detailedSection, opacity, marginBottom: getSpacing('7rem', '5rem', '4rem', '3rem') }}
            variants={itemVariants}
          >
            <div style={styles.descriptionCard}>
              <h2 style={{ ...styles.sectionTitle, fontSize: getFontSize('2.2rem', '1.8rem', '1.6rem', '1.4rem') }}>
                Une approche <span style={styles.titleAccent}>sur mesure</span>
                <span style={styles.titleDecoration} />
              </h2>
              
              <div style={{ ...styles.descriptionContent, columnCount: isMobile ? 1 : 2, columnGap: '3rem' }}>
                <p style={styles.contentText}>
                  Le droit des sociétés est le socle juridique de toute aventure entrepreneuriale. 
                  Nous assistons nos clients dans le choix de la forme juridique la plus adaptée, la rédaction de statuts 
                  personnalisés et l'élaboration de pactes d'associés pour prévenir les conflits futurs.
                </p>
                <p style={styles.contentText}>
                  Au-delà de la création, notre cabinet assure le suivi juridique courant (secrétariat juridique, approbation des comptes) 
                  et intervient dans les opérations exceptionnelles telles que les augmentations de capital, les fusions, 
                  ou les levées de fonds, en garantissant la sécurité juridique de chaque opération.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Domaines d'Intervention */}
          <motion.div style={styles.domainesSection} variants={containerVariants}>
            <motion.div style={styles.sectionHeader} variants={itemVariants}>
              <span style={styles.sectionBadge}>Nos Compétences</span>
              <h2 style={{ ...styles.sectionTitle, fontSize: getFontSize('2.5rem', '2rem', '1.8rem', '1.6rem') }}>
                Domaines d'intervention
              </h2>
              <p style={styles.sectionSubtitle}>
                Une maîtrise technique couvrant tous les aspects du droit des affaires.
              </p>
            </motion.div>

            <div style={{
              ...styles.domainesGrid,
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(320px, 1fr))',
              gap: getSpacing('2rem', '1.5rem', '1rem', '1rem'),
            }}>
              {domainesIntervention.map((domaine, index) => (
                <motion.div
                  key={index}
                  style={styles.domaineCard}
                  variants={itemVariants}
                  whileHover={{ y: -5, borderColor: '#C1A68D', backgroundColor: 'rgba(26,26,26,0.8)' }}
                >
                  <div style={styles.domaineIconWrapper}>
                    <div style={styles.iconContent}>{domaine.icon}</div>
                  </div>
                  <h3 style={{ ...styles.domaineTitle, fontSize: getFontSize('1.2rem', '1.1rem', '1rem', '1rem') }}>
                    {domaine.title}
                  </h3>
                  <p style={styles.domaineDescription}>
                    {domaine.description}
                  </p>
                  <FaChevronRight style={styles.domaineArrow} />
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* CTA Section */}
          <motion.div style={{...styles.ctaSection, marginTop: '6rem'}} variants={itemVariants}>
             <div style={styles.ctaContent}>
                <h2 style={{...styles.ctaTitle, fontSize: getFontSize('2.2rem', '1.8rem', '1.6rem', '1.4rem')}}>
                  Besoin d'un accompagnement juridique ?
                </h2>
                <p style={styles.ctaText}>
                  Nos avocats sont à votre disposition pour analyser votre situation et vous proposer des solutions concrètes et sécurisées.
                </p>
                <div style={styles.ctaButtons}>
                  <Link to="/contact" style={{ textDecoration: 'none' }}>
                    <motion.button 
                      style={styles.ctaButtonPrimary}
                      whileHover={{ backgroundColor: '#A88D73' }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <FaPhoneAlt style={{ marginRight: '8px' }} />
                      Prendre Rendez-vous
                    </motion.button>
                  </Link>
                </div>
             </div>
          </motion.div>

        </div>
      </motion.section>
    </>
  );
};

const styles = {
  section: {
    background: '#0A0A0A',
    color: '#F5F0E6',
    position: 'relative',
    overflow: 'hidden',
    minHeight: '100vh',
    fontFamily: "system-ui, -apple-system, sans-serif",
  },
  
  noiseOverlay: {
    position: 'absolute',
    inset: 0,
    backgroundImage: "url('https://www.transparenttextures.com/patterns/stardust.png')",
    opacity: 0.15,
    mixBlendMode: 'overlay',
    pointerEvents: 'none',
    zIndex: 1,
  },
  
  glowOrb1: {
    position: 'absolute',
    width: '600px', height: '600px',
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(193, 166, 141, 0.05) 0%, transparent 70%)',
    top: '-10%', right: '-10%',
    zIndex: 0,
  },
  
  glowOrb2: {
    position: 'absolute',
    width: '500px', height: '500px',
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(193, 166, 141, 0.03) 0%, transparent 70%)',
    bottom: '10%', left: '-10%',
    zIndex: 0,
  },
  
  container: {
    position: 'relative',
    zIndex: 2,
    maxWidth: '1300px',
    margin: '0 auto',
    padding: '8rem 2rem 4rem',
  },
  
  heroSection: {
    position: 'relative',
  },
  
  badge: {
    display: 'inline-block',
    border: '1px solid #C1A68D',
    background: 'rgba(193, 166, 141, 0.05)',
    color: '#C1A68D',
    padding: '0.4rem 1.2rem',
    borderRadius: '2px',
    fontSize: '0.85rem',
    fontWeight: '500',
    letterSpacing: '1px',
    textTransform: 'uppercase',
    marginBottom: '1.5rem',
  },
  
  heroContent: {
    display: 'grid',
    alignItems: 'center',
  },
  
  heroTextContainer: {
    position: 'relative',
    zIndex: 2,
  },
  
  mainTitle: {
    margin: 0,
    fontWeight: '700',
    lineHeight: 1.1,
    letterSpacing: '1px',
    textTransform: 'uppercase',
  },
  
  titleGradient: {
    color: '#F5F0E6',
  },
  
  subtitle: {
    color: '#C1A68D',
    fontWeight: '300',
    lineHeight: 1.4,
    letterSpacing: '0.5px',
  },
  
  subtitleAccent: {
    color: '#F5F0E6',
    fontWeight: '500',
  },
  
  heroText: {
    lineHeight: 1.7,
    color: '#D4C5B0',
    maxWidth: '650px',
    fontWeight: '300',
  },
  
  textHighlight: {
    color: '#C1A68D',
    fontWeight: '500',
  },
  
  statsContainer: {
    display: 'flex',
    gap: '2rem',
    flexWrap: 'wrap',
    borderTop: '1px solid rgba(193, 166, 141, 0.2)',
    paddingTop: '2rem',
  },
  
  statItem: {
    flex: 1,
    minWidth: '120px',
  },
  
  statIcon: {
    fontSize: '1.5rem',
    color: '#C1A68D',
    marginBottom: '0.8rem',
  },
  
  statValue: {
    color: '#F5F0E6',
    fontSize: '2rem',
    fontWeight: '700',
    fontFamily: "'Times New Roman', serif",
    marginBottom: '0.3rem',
  },
  
  statLabel: {
    color: '#D4C5B0',
    fontSize: '0.85rem',
    fontWeight: '500',
    textTransform: 'uppercase',
    letterSpacing: '1px',
  },
  
  heroImage: {
    position: 'relative',
  },
  
  imageWrapper: {
    position: 'relative',
    overflow: 'hidden',
    borderRadius: '4px',
    border: '1px solid rgba(193, 166, 141, 0.2)',
  },
  
  imageOverlay: {
    position: 'absolute',
    inset: 0,
    background: 'linear-gradient(45deg, rgba(10,10,10,0.6), rgba(193,166,141,0.1))',
    zIndex: 1,
    pointerEvents: 'none',
  },
  
  image: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    filter: 'grayscale(30%) contrast(110%)',
  },
  
  floatingBadge: {
    position: 'absolute',
    bottom: '0',
    right: '0',
    background: '#1A1A1A',
    borderTop: '1px solid #C1A68D',
    borderLeft: '1px solid #C1A68D',
    color: '#C1A68D',
    padding: '1rem 1.5rem',
    fontWeight: '500',
    fontSize: '0.9rem',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    display: 'flex',
    alignItems: 'center',
    gap: '0.8rem',
    zIndex: 3,
  },

  badgeIconLarge: {
    fontSize: '1.2rem',
  },
  
  detailedSection: {
    position: 'relative',
  },
  
  descriptionCard: {
    background: 'rgba(26, 26, 26, 0.4)',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(193, 166, 141, 0.15)',
    borderRadius: '4px',
    padding: '4rem 3rem',
  },
  
  sectionTitle: {
    fontWeight: '700',
    color: '#F5F0E6',
    textTransform: 'uppercase',
    letterSpacing: '1.5px',
    marginBottom: '2rem',
    position: 'relative',
    display: 'inline-block',
  },

  titleAccent: {
    color: '#C1A68D',
  },
  
  titleDecoration: {
    position: 'absolute',
    bottom: '-10px',
    left: '0',
    width: '40px',
    height: '2px',
    background: '#C1A68D',
  },
  
  contentText: {
    lineHeight: '1.7',
    color: '#D4C5B0',
    marginBottom: '1.5rem',
    fontWeight: '300',
    fontSize: '1rem',
  },
  
  sectionHeader: {
    textAlign: 'center',
    marginBottom: '4rem',
  },
  
  sectionBadge: {
    display: 'inline-block',
    color: '#C1A68D',
    fontSize: '0.85rem',
    fontWeight: '500',
    letterSpacing: '1px',
    textTransform: 'uppercase',
    marginBottom: '1rem',
  },
  
  sectionSubtitle: {
    color: '#D4C5B0',
    maxWidth: '600px',
    margin: '1rem auto 0',
    lineHeight: '1.6',
    fontWeight: '300',
  },
  
  domainesGrid: {
    display: 'grid',
  },
  
  domaineCard: {
    position: 'relative',
    background: 'rgba(26, 26, 26, 0.4)',
    backdropFilter: 'blur(10px)',
    borderRadius: '4px',
    padding: '2.5rem',
    transition: 'all 0.3s ease',
    border: '1px solid rgba(193, 166, 141, 0.15)',
    display: 'flex',
    flexDirection: 'column',
  },
  
  domaineIconWrapper: {
    width: '50px',
    height: '50px',
    marginBottom: '1.5rem',
    borderRadius: '2px',
    background: 'rgba(193, 166, 141, 0.05)',
    border: '1px solid rgba(193, 166, 141, 0.2)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  
  iconContent: {
    fontSize: '1.5rem',
    color: '#C1A68D',
  },
  
  domaineTitle: {
    fontWeight: '600',
    color: '#F5F0E6',
    marginBottom: '1rem',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  
  domaineDescription: {
    color: '#D4C5B0',
    lineHeight: '1.6',
    margin: '0 0 1.5rem 0',
    fontWeight: '300',
    fontSize: '0.95rem',
    flexGrow: 1,
  },
  
  domaineArrow: {
    color: '#C1A68D',
    fontSize: '1rem',
    alignSelf: 'flex-start',
  },

  ctaSection: {
    background: 'rgba(26, 26, 26, 0.6)',
    border: '1px solid rgba(193, 166, 141, 0.2)',
    borderRadius: '4px',
    padding: '4rem 2rem',
    textAlign: 'center',
  },

  ctaContent: {
    maxWidth: '700px',
    margin: '0 auto',
  },

  ctaTitle: {
    color: '#F5F0E6',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    marginBottom: '1.5rem',
  },

  ctaText: {
    color: '#D4C5B0',
    fontWeight: '300',
    lineHeight: '1.6',
    marginBottom: '2.5rem',
  },

  ctaButtons: {
    display: 'flex',
    justifyContent: 'center',
  },

  ctaButtonPrimary: {
    background: '#C1A68D',
    color: '#1A1A1A',
    border: 'none',
    padding: '1rem 2.5rem',
    borderRadius: '2px',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    transition: 'background 0.3s ease',
  }
};

export default DroitSocietes;